
import * as React from "react";

export type HeroButtonStyle = 
  | "original" 
  | "ghost" 
  | "ghost-blue" 
  | "ghost-blue-black"
  | "glass" 
  | "glass-white"
  | "minimal" 
  | "soft" 
  | "gradient-border" 
  | "shimmer"
  | "white-blue"
  | "white-blue-border"
  | "neu-pill"
  | "turquoise-pill"
  | "flat";

export type HeadlineStyle = 
  | "gradient" 
  | "solid-primary" 
  | "white" 
  | "subtle-gray"
  | "outline-primary" 
  | "outline-white"
  | "outline-gradient"
  | "outline-glow-white"
  | "transparent-gradient"
  | "underline" 
  | "highlight"
  | "all-outline-white"
  | "split-outline-white"
  | "split-white-outline";

type DebugControlsContextValue = {
  debugEnabled: boolean;
  setDebugEnabled: (enabled: boolean) => void;
  showStudioMenu: boolean;
  setShowStudioMenu: (enabled: boolean) => void;
  heroButtonStyle: HeroButtonStyle;
  setHeroButtonStyle: (style: HeroButtonStyle) => void;
  headlineStyle: HeadlineStyle;
  setHeadlineStyle: (style: HeadlineStyle) => void;
};

const DebugControlsContext = React.createContext<DebugControlsContextValue | null>(null);

export function DebugControlsProvider({ children }: { children: React.ReactNode }) {
  const [debugEnabled, setDebugEnabled] = React.useState(false);
  const [showStudioMenu, setShowStudioMenu] = React.useState(false);
  // Set default to 'ghost-blue-black' as requested
  const [heroButtonStyle, setHeroButtonStyle] = React.useState<HeroButtonStyle>("ghost-blue-black");
  const [headlineStyle, setHeadlineStyle] = React.useState<HeadlineStyle>("split-white-outline");

  const value = React.useMemo(
    () => ({
      debugEnabled,
      setDebugEnabled,
      showStudioMenu,
      setShowStudioMenu,
      heroButtonStyle,
      setHeroButtonStyle,
      headlineStyle,
      setHeadlineStyle,
    }),
    [debugEnabled, showStudioMenu, heroButtonStyle, headlineStyle]
  );

  return <DebugControlsContext.Provider value={value}>{children}</DebugControlsContext.Provider>;
}

export function useDebugControls() {
  const ctx = React.useContext(DebugControlsContext);
  if (!ctx) {
    throw new Error("useDebugControls must be used within DebugControlsProvider");
  }
  return ctx;
}
